import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Progress, Spin, message } from 'antd';
import { withRouter } from 'react-router-dom';

import heroOne from '../../assets/character/hero-1.jpg';
import heroTwo from '../../assets/character/hero-2.jpg';
import heroThree from '../../assets/character/hero-3.jpg';
import heroFour from '../../assets/character/hero-4.jpg';
import heroFive from '../../assets/character/hero-5.jpg';
import galleryOne from '../../assets/character/gallery-1.jpg';
import galleryTwo from '../../assets/character/gallery-2.jpg';
import galleryThree from '../../assets/character/gallery-3.jpg';
import galleryFour from '../../assets/character/gallery-4.jpg';
import galleryFive from '../../assets/character/gallery-5.jpg';
import { characterApi } from '../../modules/api';
import { triggerDownloadFromUrl } from '../../utils/download';

import './character.css';

const heroCards = [
  { image: heroOne, label: 'Artist portrait', modifier: 'character-hero__card--left' },
  { image: heroTwo, label: 'Photographer portrait', modifier: 'character-hero__card--center' },
  { image: heroThree, label: 'Ceramic artist portrait', modifier: 'character-hero__card--right' },
  { image: heroFour, label: 'Sketch artist portrait', modifier: 'character-hero__card--lower-left' },
  { image: heroFive, label: 'Studio portrait', modifier: 'character-hero__card--lower-right' },
];

const galleryCards = [
  { image: galleryOne, label: 'Editorial portrait 1' },
  { image: galleryTwo, label: 'Editorial portrait 2' },
  { image: galleryThree, label: 'Editorial portrait 3' },
  { image: galleryFour, label: 'Editorial portrait 4' },
  { image: galleryFive, label: 'Editorial portrait 5' },
];

const MIN_UPLOADS = 1;
const MAX_UPLOADS = 20;
const ALLOWED_UPLOAD_TYPES = new Set(['image/jpeg', 'image/png']);

const generationSteps = [
  {
    key: 'upload',
    title: 'Uploading images',
    description: 'Sending your selected photos securely.',
  },
  {
    key: 'quality',
    title: 'Checking image quality',
    description: 'Reviewing clear faces, angles, duplicates, and usable references.',
  },
  {
    key: 'references',
    title: 'Preparing face references',
    description: 'Keeping top-quality source photos for Character Swap.',
  },
  {
    key: 'document',
    title: 'Generating Character ID image',
    description: 'Creating one clean formal portrait on a white background.',
  },
  {
    key: 'swap',
    title: 'Checking Character Swap availability',
    description: 'Running a provider compatibility check with the approved references.',
  },
  {
    key: 'finalize',
    title: 'Saving character',
    description: 'Saving the result and refreshing your character library.',
  },
];

const createInitialProgress = () => ({
  percent: 0,
  activeStepKey: 'upload',
  message: null,
});

const backendProgressStepMap = {
  quality: 'quality',
  references: 'references',
  document: 'document',
  swap: 'swap',
  finalize: 'finalize',
  ready: 'finalize',
  failed: 'finalize',
};

const resolveCharacterProgress = (character) => {
  const progress = character && character.progress ? character.progress : {};
  const stage = progress.stage || (character && character.status === 'ready' ? 'ready' : null);
  const activeStepKey = backendProgressStepMap[stage] || 'quality';
  const percent = Math.max(0, Math.min(100, Number(progress.percent) || 0));

  if (character && character.status === 'ready') {
    return {
      percent: 100,
      activeStepKey: 'finalize',
      message: progress.message || null,
    };
  }

  return {
    percent: Math.max(percent, activeStepKey === 'quality' ? 46 : percent),
    activeStepKey,
    message: progress.message || null,
  };
};

const formatCharacterDate = (value) => {
  if (!value) {
    return 'Character';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Character';
  }

  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getSwapBlockedMessage = (reason) => {
  const normalized = String(reason || '').toLowerCase();
  if (normalized.includes('sensitive information')) {
    return 'This character cannot be used in Character Swap because the provider blocked swap output for likely public-figure identity.';
  }
  return 'This character cannot be used in Character Swap right now.';
};

const getCharacterPreviewAsset = (character) => {
  if (!character) {
    return null;
  }

  const candidates = [
    character.coverAvatar,
    ...(Array.isArray(character.avatarImages) ? character.avatarImages : []),
    ...(Array.isArray(character.runtimeReferenceImages) ? character.runtimeReferenceImages : []),
    ...(Array.isArray(character.uploadedSourceImages) ? character.uploadedSourceImages : []),
  ];

  return candidates.find((asset) => asset && asset.url) || null;
};

const createUploadEntry = (file, index) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
  file,
  previewUrl: URL.createObjectURL(file),
  name: file.name,
  size: file.size,
});

const Character = ({ history, location }) => {
  const [characters, setCharacters] = useState([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedUploads, setSelectedUploads] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [preparingCharacterId, setPreparingCharacterId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(createInitialProgress);
  const [detailCharacter, setDetailCharacter] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isDownloadingCharacterId, setIsDownloadingCharacterId] = useState(false);
  const [libraryNotice, setLibraryNotice] = useState('');
  const fileInputRef = useRef(null);
  const uploadsRef = useRef([]);
  const activeUploadsCountRef = useRef(0);

  const isCreateRoute = location && location.pathname === '/character/create';

  const revokeUploadEntries = useCallback((entries = []) => {
    entries.forEach((entry) => {
      if (entry && entry.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl);
      }
    });
  }, []);

  const resetCreateFlow = useCallback(() => {
    revokeUploadEntries(uploadsRef.current);
    uploadsRef.current = [];
    setSelectedUploads([]);
    setIsSelecting(false);
    setIsPreparing(false);
    setPreparingCharacterId(null);
    setGenerationProgress(createInitialProgress());
  }, [revokeUploadEntries]);

  useEffect(() => {
    uploadsRef.current = selectedUploads;
    activeUploadsCountRef.current = selectedUploads.length;
  }, [selectedUploads]);

  useEffect(() => () => {
    revokeUploadEntries(uploadsRef.current);
  }, [revokeUploadEntries]);

  useEffect(() => {
    if (!isCreateRoute && uploadsRef.current.length) {
      resetCreateFlow();
    }
  }, [isCreateRoute, resetCreateFlow]);

  const applyBackendProgress = useCallback((character) => {
    if (!character) {
      return;
    }
    setGenerationProgress(resolveCharacterProgress(character));
  }, []);

  const loadCharacters = useCallback(async () => {
    setIsListLoading(true);
    setListError('');
    try {
      const { data } = await characterApi.listCharacters();
      setCharacters(Array.isArray(data && data.items) ? data.items : []);
    } catch (error) {
      const nextMessage =
        (error && error.response && error.response.data && error.response.data.message) ||
        'Unable to load characters right now.';
      setListError(nextMessage);
    } finally {
      setIsListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  useEffect(() => {
    if (isPreparing || preparingCharacterId || activeUploadsCountRef.current) {
      return;
    }

    const processingCharacter = characters.find((character) => (
      character && character.id && character.status === 'processing'
    ));

    if (!processingCharacter) {
      return;
    }

    setPreparingCharacterId(processingCharacter.id);
    setIsPreparing(true);
    applyBackendProgress(processingCharacter);
  }, [applyBackendProgress, characters, isPreparing, preparingCharacterId]);

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleOpenCreateFlow = useCallback(() => {
    setLibraryNotice('');
    history.push('/character/create');
  }, [history]);

  const handleSelectFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) {
      return;
    }

    setIsSelecting(true);
    try {
      const invalidFiles = files.filter((file) => !ALLOWED_UPLOAD_TYPES.has(file.type));
      if (invalidFiles.length) {
        message.error('Only JPG and PNG images are allowed.');
        return;
      }

      const availableSlots = Math.max(MAX_UPLOADS - uploadsRef.current.length, 0);
      if (!availableSlots) {
        message.warning(`You can upload up to ${MAX_UPLOADS} images per character.`);
        return;
      }

      const selectedFiles = files.slice(0, availableSlots);
      const nextEntries = selectedFiles.map((file, index) => createUploadEntry(file, index));

      setSelectedUploads((current) => [...current, ...nextEntries]);

      if (files.length > selectedFiles.length) {
        message.info(`Only ${selectedFiles.length} image${selectedFiles.length === 1 ? '' : 's'} were added. Maximum is ${MAX_UPLOADS}.`);
      } else {
        message.success(`${selectedFiles.length} image${selectedFiles.length === 1 ? '' : 's'} selected.`);
      }
    } finally {
      setIsSelecting(false);
    }
  }, []);

  const handleRemoveUpload = useCallback((uploadId) => {
    setSelectedUploads((current) => {
      const removed = current.filter((entry) => entry.id === uploadId);
      const next = current.filter((entry) => entry.id !== uploadId);
      revokeUploadEntries(removed);
      return next;
    });
  }, [revokeUploadEntries]);

  const handleGenerateCharacter = useCallback(async () => {
    if (selectedUploads.length < MIN_UPLOADS || isPreparing) {
      return;
    }

    let createdCharacterId = null;
    let preparationStarted = false;
    setIsPreparing(true);
    setPreparingCharacterId(null);
    setGenerationProgress({
      percent: 3,
      activeStepKey: 'upload',
      message: 'Sending your selected photos securely.',
    });

    try {
      const { data: created } = await characterApi.createCharacter();
      createdCharacterId = created && created.id ? created.id : null;

      if (!createdCharacterId) {
        throw new Error('Unable to create character draft.');
      }

      await characterApi.uploadCharacterImages(
        createdCharacterId,
        selectedUploads.map((entry) => entry.file),
        {
          onUploadProgress: (event) => {
            if (!event || !event.total) {
              setGenerationProgress({
                percent: 22,
                activeStepKey: 'upload',
                message: 'Sending your selected photos securely.',
              });
              return;
            }
            const uploadPercent = Math.round((event.loaded / event.total) * 100);
            setGenerationProgress({
              percent: Math.min(45, 8 + Math.round(uploadPercent * 0.37)),
              activeStepKey: 'upload',
              message: 'Sending your selected photos securely.',
            });
          },
        },
      );

      setGenerationProgress({
        percent: 46,
        activeStepKey: 'quality',
        message: 'Waiting for backend quality checks to start.',
      });
      const { data: preparation } = await characterApi.prepareCharacter(createdCharacterId);
      preparationStarted = true;
      setPreparingCharacterId(createdCharacterId);
      applyBackendProgress(preparation && preparation.character ? preparation.character : null);
      setLibraryNotice('');
    } catch (error) {
      if (createdCharacterId && !preparationStarted) {
        try {
          await characterApi.deleteCharacter(createdCharacterId);
        } catch (cleanupError) {
          // Ignore cleanup failures and surface the original error to the user.
        }
      }

      const nextMessage =
        (error && error.response && error.response.data && error.response.data.message) ||
        error.message ||
        'Unable to create character.';
      message.error(nextMessage);
    } finally {
      if (!preparationStarted) {
        setIsPreparing(false);
        setPreparingCharacterId(null);
      }
    }
  }, [
    applyBackendProgress,
    isPreparing,
    selectedUploads,
  ]);

  useEffect(() => {
    if (!preparingCharacterId || !isPreparing) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;

    const pollCharacter = async () => {
      try {
        const { data } = await characterApi.fetchCharacter(preparingCharacterId);
        if (cancelled) {
          return;
        }

        applyBackendProgress(data);

        if (data.status === 'ready') {
          message.success('Character created.');
          history.replace('/character');
          resetCreateFlow();
          await loadCharacters();
          return;
        }

        if (data.status === 'failed') {
          const nextMessage = data.failureReason || 'Unable to create character.';
          message.error(nextMessage);
          setIsPreparing(false);
          setPreparingCharacterId(null);
          await loadCharacters();
          return;
        }

        timeoutId = window.setTimeout(pollCharacter, 2500);
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error && error.response && error.response.status === 404) {
          message.error('Character generation failed. Please try again.');
          setIsPreparing(false);
          setPreparingCharacterId(null);
          await loadCharacters();
          return;
        }
        const nextMessage =
          (error && error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Unable to check character progress.';
        message.error(nextMessage);
        setIsPreparing(false);
        setPreparingCharacterId(null);
      }
    };

    pollCharacter();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    applyBackendProgress,
    history,
    isPreparing,
    loadCharacters,
    preparingCharacterId,
    resetCreateFlow,
  ]);

  const handleCharacterOpen = useCallback(async (characterId) => {
    if (!characterId) {
      return;
    }

    setIsDetailLoading(true);
    setDetailOpen(true);
    try {
      const { data } = await characterApi.fetchCharacter(characterId);
      setDetailCharacter(data);
    } catch (error) {
      const nextMessage =
        (error && error.response && error.response.data && error.response.data.message) ||
        'Unable to load character details.';
      message.error(nextMessage);
      setDetailOpen(false);
    } finally {
      setIsDetailLoading(false);
    }
  }, []);

  const handleDeleteCharacter = useCallback(async () => {
    if (!detailCharacter || !detailCharacter.id) {
      return;
    }

    try {
      await characterApi.deleteCharacter(detailCharacter.id);
      message.success('Character deleted.');
      setDetailOpen(false);
      setDetailCharacter(null);
      await loadCharacters();
    } catch (error) {
      const nextMessage =
        (error && error.response && error.response.data && error.response.data.message) ||
        'Unable to delete character.';
      message.error(nextMessage);
    }
  }, [detailCharacter, loadCharacters]);

  const handleDownloadCharacterId = useCallback(async () => {
    const avatarImage = getCharacterPreviewAsset(detailCharacter);

    if (!avatarImage || !avatarImage.url || isDownloadingCharacterId) {
      return;
    }

    try {
      setIsDownloadingCharacterId(true);
      await triggerDownloadFromUrl(
        avatarImage.url,
        avatarImage.filename
          || avatarImage.originalName
          || `character-id-${detailCharacter && detailCharacter.id ? detailCharacter.id : 'image'}.png`,
      );
    } catch (error) {
      message.error('Unable to download image. Please try again.');
    } finally {
      setIsDownloadingCharacterId(false);
    }
  }, [detailCharacter, isDownloadingCharacterId]);

  const handleUseCharacterInGenerator = useCallback(() => {
    if (!detailCharacter || !detailCharacter.id || detailCharacter.swapStatus === 'blocked') {
      return;
    }

    history.push('/ai', {
      selectedCharacterId: detailCharacter.id,
      focusTab: 'generate',
    });
  }, [detailCharacter, history]);

  const uploadedCount = selectedUploads.length;
  const slotsLeftCount = Math.max(MAX_UPLOADS - uploadedCount, 0);
  const hasCharacters = characters.length > 0;
  const canGenerate = uploadedCount >= MIN_UPLOADS
    && uploadedCount <= MAX_UPLOADS
    && !isPreparing
    && !isSelecting;

  const stepStates = useMemo(() => {
    const hasUploads = uploadedCount > 0;
    const readyToGenerate = uploadedCount >= MIN_UPLOADS;

    return [
      {
        number: 1,
        title: 'Select Images',
        description: `Choose up to ${MAX_UPLOADS} JPG or PNG photos.`,
        status: hasUploads ? 'done' : 'active',
      },
      {
        number: 2,
        title: 'Review Images',
        description: 'Add more or remove any weak shots before upload.',
        status: hasUploads ? (readyToGenerate ? 'done' : 'active') : 'idle',
      },
      {
        number: 3,
        title: 'Generate',
        description: 'Your own reference character',
        status: isPreparing ? 'active' : (readyToGenerate ? 'ready' : 'idle'),
      },
    ];
  }, [isPreparing, uploadedCount]);

  const detailSourceImages = detailCharacter && Array.isArray(detailCharacter.uploadedSourceImages)
    ? detailCharacter.uploadedSourceImages
    : [];
  const primaryDetailAvatar = getCharacterPreviewAsset(detailCharacter);
  const activeGenerationStep = generationSteps.find((step) => step.key === generationProgress.activeStepKey)
    || generationSteps[0];

  return (
    <div className={`character-page${isPreparing ? ' character-page--busy' : ''}`}>
      {isCreateRoute ? (
        <section className="character-create">
          <div className="character-create__panel character-create__panel--wizard">
            <div className="character-create__intro">
              <span className="character-create__eyebrow">Three-step Character Creation</span>
              <h1>Create your character reference</h1>
            </div>

            <div className="character-create__steps">
              {stepStates.map((step) => (
                <article
                  key={step.number}
                  className={`character-step character-step--${step.status}`}
                >
                  <span className="character-step__number">{step.number}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="character-create__panel-layout">
              <div className="character-create__content">
                <div className="character-create__rule">
                  <div className="character-create__rule-icon character-create__rule-icon--good">
                    <i className="fa-solid fa-check" aria-hidden="true" />
                  </div>
                  <div>
                    <h2>Upload to Make You Alive</h2>
                    <p>Start with one clear portrait</p>
                    <p>Add up to 20 photos; a few different angles help preserve your likeness.</p>
                    <p>Show different angles, clear faces, same person only.</p>
                  </div>
                </div>

                <div className="character-create__rule">
                  <div className="character-create__rule-icon character-create__rule-icon--bad">
                    <i className="fa-solid fa-ban" aria-hidden="true" />
                  </div>
                  <div>
                    <h3>Don&apos;t Upload</h3>
                    <p>No public figures, duplicates, group photos, pets, filters, masks, or face covers</p>
                    <p>Keep it real. Keep it alive.</p>
                  </div>
                </div>

                <div className="character-create__rule character-create__rule--tip">
                  <div className="character-create__rule-icon character-create__rule-icon--tip" aria-hidden="true">
                    <span className="character-create__sparkle-main">✦</span>
                    <span className="character-create__sparkle-mini">✦</span>
                  </div>
                  <div>
                    <h3>
                      SO
                      <span className="character-create__brand-accent">A</span>
                      L<span className="character-create__brand-accent">I</span>VE Tip
                    </h3>
                    <p>Natural photos = better vibes</p>
                  </div>
                </div>
              </div>

              <div className="character-create__actions">
                <Button
                  type="primary"
                  className="character-create__upload"
                  onClick={openFilePicker}
                  loading={isSelecting}
                  disabled={slotsLeftCount <= 0 || isPreparing}
                >
                  <i className="fa-regular fa-images" aria-hidden="true" />
                  {uploadedCount ? 'Add Images' : 'Select Images'}
                </Button>
              </div>
            </div>

            <div className="character-create__toolbar">
              <Button
                type="primary"
                className="character-create__submit"
                disabled={!canGenerate}
                loading={isPreparing}
                onClick={handleGenerateCharacter}
              >
                <span className="character-create__button-sparkle" aria-hidden="true">
                  <span className="character-create__button-sparkle-main">✦</span>
                  <span className="character-create__button-sparkle-mini">✦</span>
                </span>
                Generate Character (30 credits)
              </Button>
            </div>

            <section className="character-uploaded character-uploaded--embedded">
              <div className="character-section__header">
                <div>
                  <h2>Review Images</h2>
                </div>
              </div>

              {uploadedCount ? (
                <div className="character-uploaded__grid">
                  {selectedUploads.map((image) => (
                    <article key={image.id} className="character-uploaded__item character-uploaded__item--editable">
                      <img src={image.previewUrl} alt={image.name || 'Character upload'} />
                      <button
                        type="button"
                        className="character-uploaded__remove"
                        onClick={() => handleRemoveUpload(image.id)}
                        disabled={isPreparing}
                        aria-label={`Remove ${image.name}`}
                      >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="character-create__empty">
                  <div className="character-create__empty-icon">
                    <i className="fa-regular fa-images" aria-hidden="true" />
                  </div>
                </div>
              )}
            </section>
          </div>
        </section>
      ) : (
        <>
          <section className="character-hero">
            <div className="character-hero__visual" aria-hidden="true">
              {heroCards.map((card) => (
                <figure
                  key={card.label}
                  className={`character-hero__card ${card.modifier}`}
                >
                  <img src={card.image} alt={card.label} />
                </figure>
              ))}
            </div>

            <div className="character-hero__copy">
              <h1>
                Make your own character with SO
                <span className="character-hero__brand-accent">A</span>
                L<span className="character-hero__brand-accent">I</span>VE.
              </h1>
              <p className="character-hero__lede">
                Turn your photos into infinite selfies in different styles.
                <br />
                Your unique look comes alive in just minutes.
              </p>
              <div className="character-hero__actions">
                <button
                  type="button"
                  className="character-page__primary"
                  onClick={handleOpenCreateFlow}
                >
                  Create Character
                </button>
              </div>
            </div>
          </section>

          {!hasCharacters ? (
            <section className="character-gallery">
              {galleryCards.map((card) => (
                <article key={card.label} className="character-gallery__item">
                  <img src={card.image} alt={card.label} />
                </article>
              ))}
            </section>
          ) : null}

          <section className="character-library">
            <div className="character-section__header">
              <div>
                <h2>Your Characters</h2>
              </div>
            </div>

            {libraryNotice ? (
              <div className="character-library__notice">{libraryNotice}</div>
            ) : null}

            {isListLoading ? (
              <div className="character-library__empty">
                <Spin />
              </div>
            ) : listError ? (
              <div className="character-library__empty">
                <p>{listError}</p>
              </div>
            ) : characters.length ? (
              <div className="character-library__grid">
                {characters.map((character) => {
                  const previewAsset = getCharacterPreviewAsset(character);
                  return (
                    <button
                      key={character.id}
                      type="button"
                      className="character-card"
                      onClick={() => handleCharacterOpen(character.id)}
                    >
                      <div className="character-card__image">
                        {previewAsset ? (
                          <img src={previewAsset.url} alt={character.displayName} />
                        ) : (
                          <div className="character-card__placeholder">
                            <i className="fa-regular fa-user" aria-hidden="true" />
                          </div>
                        )}
                        {character.swapStatus === 'blocked' ? (
                          <span className="character-card__badge character-card__badge--blocked">
                            <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                            <span className="sr-only">Swap unavailable</span>
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="character-library__empty">
                <p>No characters yet. Create your first one from the button above.</p>
              </div>
            )}
          </section>
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
        multiple
        className="character-hidden-input"
        disabled={isPreparing}
        onChange={(event) => {
          handleSelectFiles(event.target.files);
          event.target.value = '';
        }}
      />

      {isPreparing ? (
        <div className="character-page__blocking-overlay" role="alert" aria-live="polite">
          <div className="character-page__blocking-card">
            <div className="character-progress__header">
              <Spin size="large" />
              <div>
                <h3>Generating Character ID</h3>
                <p>{generationProgress.message || activeGenerationStep.description}</p>
              </div>
            </div>

            <div className="character-progress__bar">
              <Progress
                percent={generationProgress.percent}
                showInfo={false}
                strokeColor="#111111"
                trailColor="#ece9f7"
              />
              <span>{generationProgress.percent}%</span>
            </div>

            <ol className="character-progress__steps">
              {generationSteps.map((step) => {
                const activeStepIndex = generationSteps.findIndex((item) => item.key === generationProgress.activeStepKey);
                const stepIndex = generationSteps.findIndex((item) => item.key === step.key);
                const status = stepIndex < activeStepIndex
                  ? 'done'
                  : (step.key === generationProgress.activeStepKey ? 'active' : 'pending');

                return (
                  <li
                    key={step.key}
                    className={`character-progress__step character-progress__step--${status}`}
                  >
                    <span className="character-progress__step-icon">
                      {status === 'done' ? (
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      ) : (
                        <i className="fa-solid fa-circle" aria-hidden="true" />
                      )}
                    </span>
                    <div>
                      <strong>{step.title}</strong>
                      <p>{step.description}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      ) : null}

      <Modal
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetailCharacter(null);
        }}
        footer={null}
        width={960}
        className="character-detail-modal"
        title={null}
        destroyOnHidden
      >
        {isDetailLoading ? (
          <div className="character-detail__loading">
            <Spin />
          </div>
        ) : detailCharacter ? (
          <div className="character-detail">
            <div className="character-detail__status-card">
              <div className="character-detail__status-top">
                <div className="character-detail__status-meta">
                  <p>{formatCharacterDate(detailCharacter.createdAt)}</p>
                </div>
              </div>

              <div className="character-detail__status-content">
                <div className="character-detail__status-copy">
                  {detailCharacter.swapStatus === 'blocked' ? (
                    <>
                      <h3>Character Swap unavailable</h3>
                      <p>{getSwapBlockedMessage(detailCharacter.swapBlockedReason)}</p>
                    </>
                  ) : (
                    <>
                      <div className="character-detail__title-row">
                        <h3>Character ID</h3>
                        <Button
                          className="character-detail__download-button"
                          icon={<i className="fa-solid fa-download" aria-hidden="true" />}
                          onClick={handleDownloadCharacterId}
                          loading={isDownloadingCharacterId}
                          disabled={!primaryDetailAvatar || !primaryDetailAvatar.url || isDownloadingCharacterId}
                        >
                          Download image
                        </Button>
                        <Button
                          type="primary"
                          className="character-detail__download-button"
                          icon={<i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />}
                          onClick={handleUseCharacterInGenerator}
                        >
                          Use in generator
                        </Button>
                      </div>
                      <p>Your generated Character ID is ready to use for compatible character workflows.</p>
                    </>
                  )}
                  <Button
                    danger
                    className="character-detail__delete-button"
                    onClick={handleDeleteCharacter}
                  >
                    Delete Character
                  </Button>
                </div>

                {primaryDetailAvatar ? (
                  <div className="character-detail__id-grid">
                    <article className="character-detail__tile character-detail__tile--id">
                      <img src={primaryDetailAvatar.url} alt={primaryDetailAvatar.originalName || 'Character ID'} />
                    </article>
                  </div>
                ) : null}
              </div>
            </div>

            <section className="character-detail__section">
              <h3>Uploaded Images</h3>
              <div className="character-detail__grid">
                {detailSourceImages.map((image) => (
                  <article key={image.id || image.url} className="character-detail__tile">
                    <img src={image.url} alt={image.originalName || 'Uploaded reference'} />
                  </article>
                ))}
              </div>
            </section>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default withRouter(Character);
